import { flowRight, get } from 'lodash';
import React from 'react';
import gql from 'graphql-tag';
import { MutationFn } from 'react-apollo';
import I18n from 'i18n-js';
import bind from '../bind';
import {
  CreateMyWatches,
  DeleteMyWatches,
  core_apimessages_Channel,
  Listing,
  Input_core_apimessages_BumpKey,
} from '../rql';
import classNames from 'classnames';
import * as elog from '../elog';

import { withUserContext, IUserContext } from './user_context_provider';
import { MParticleEventName, ProductActionType, trackEvent } from '../elog/mparticle_tracker';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';
import { getWatchNudgeText } from '../nudge_manager';
import { RCIcon } from '../rc/RCIcon';
import StarEmptySvg from '../../design-system/icons/core/star-empty.svg?component';
import StarFilledSvg from '../../design-system/icons/core/star-filled.svg?component';
import HeartEmptySvg from '../../design-system/icons/core/heart-empty.svg?component';
import HeartFilledSvg from '../../design-system/icons/core/heart-filled.svg?component';
import { withGraphql } from '../with_graphql';

export enum WatchBadgeParentComponent {
  ItemPageActions = 'ItemPage', // existing value used for the item page watch button (item2_pricing.tsx)
  ItemPageImage = 'ItemPage - Image', // image carousel on item page
  MarketplaceListViewAction = 'GridCardRow',
  MarketplaceListViewIcon = 'Marketplace List View - Watch Icon',
}

interface IProps {
  listing: Listing;
  // if watchSource is not provided, watchSource will default to 'Reverb'
  watchSource?: core_apimessages_Channel;
  createWatch?: MutationFn<CreateMyWatches.Mutation, CreateMyWatches.Variables>;
  deleteWatch?: MutationFn<DeleteMyWatches.Mutation, DeleteMyWatches.Variables>;
  onError?: (e: Error) => void;
  handleLoggedOutUser?: (evt) => any;
  handleWatchCreated?: () => any;
  displayStyle: 'icon' | 'button' | 'text';
  enableNudge?: boolean;
  position?: number;
  parentComponentName?: string;
}

interface IState {
  watching: boolean;
}

const COMPONENT_NAME = 'watch_badge';
export const WATCH_PROMPT_PARAM = 'watch_id';

function getBumpKey(listing: Listing): Input_core_apimessages_BumpKey {
  if (!listing.bumpKey?.key) {
    return null;
  }

  return {
    key: listing.bumpKey.key,
  };
}

export const WatchBadgeFragment = gql`
fragment WatchBadgeData on Listing {
  _id
  id
  watching
}
`;

export const createWatch =
  withGraphql<IProps, CreateMyWatches.Mutation, CreateMyWatches.Variables>(
    gql`
      mutation CreateMyWatches($input: Input_core_apimessages_WatchesCreateRequest) {
        createWatch(input: $input) {
          id
          listing {
            id
            _id
            watching
          }
        }
      }
    `,
    {
      name: 'createWatch',
      options: ownProps => ({
        variables: {
          input: {
            listingId: ownProps.listing.id,
            channel: ownProps.watchSource,
            bumpKey: getBumpKey(ownProps.listing),
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          createWatch: {
            __typename: 'core_apimessages_UpdateWatchResponse',
            id: -1,
            listing: {
              id: ownProps.listing.id,
              _id: ownProps.listing._id,
              watching: true,
              __typename: 'Listing',
            },
          },
        },
      }),
    },
  );

export const deleteWatch =
  withGraphql<IProps, DeleteMyWatches.Mutation, DeleteMyWatches.Variables>(
    gql`
      mutation DeleteMyWatches($input: Input_core_apimessages_WatchesDeleteRequest) {
        deleteWatch(input: $input) {
          id
          listing {
            _id
            id
            watching
          }
        }
      }
    `,
    {
      name: 'deleteWatch',
      options: ownProps => ({
        variables: {
          input: { listingId: ownProps.listing.id },
        },
        optimisticResponse: {
          deleteWatch: {
            __typename: 'core_apimessages_UpdateWatchResponse',
            id: -1,
            listing: {
              id: ownProps.listing.id,
              _id: ownProps.listing._id,
              watching: false,
              __typename: 'Listing',
            },
          },
        },
      }),
    },
  );

export class WatchBadge extends React.Component<IProps & IUserContext, IState> {
  static defaultProps = {
    onError: () => null,
    displayStyle: 'button',
  };

  componentDidMount() {
    if (!this.props.user?.loggedOut) {
      const watchId = localStorage.getItem('watch_id');
      if (watchId === this.props.listing.id) {
        localStorage.removeItem('watch_id');
        if (!this.props.listing.watching) {
          this.handleWatch();
        }
      }
    }
  }

  async handleWatch() {
    const { watching } = this.props.listing;
    const productActionType = watching ?
      ProductActionType.RemoveFromWishlist : ProductActionType.AddToWishlist;
    const eventName = watching ?
      MParticleEventName.RemoveFromWishlist : MParticleEventName.AddToWishlist;

    try {
      if (this.props.listing.watching) {
        await this.props.deleteWatch();
      } else {
        await this.props.createWatch();
      }

      trackEvent({
        productActionType,
        eventName,
        listing: this.props.listing,
        position: this.props.position,
        componentName: this.props.parentComponentName,
      });

      if (this.props.handleWatchCreated) {
        this.props.handleWatchCreated();
      }
    } catch (e) {
      this.props.onError(e);
      elog.error(
        e.message,
        { eventName, componentName: COMPONENT_NAME, listingId: this.props.listing.id },
        e,
      );
    }
  }

  getTitle() {
    if (this.props.listing.watching) return I18n.t('commons.watchBadge.unwatch');
    return I18n.t('commons.watchBadge.watch');
  }

  userLoggedOut() {
    return !!get(this.props, 'user.loggedOut', true);
  }

  @bind
  handleClick(evt) {
    evt.preventDefault();
    evt.stopPropagation();

    if (this.userLoggedOut() && !!this.props.handleLoggedOutUser) {
      trackEvent({
        productActionType: ProductActionType.Click,
        eventName: MParticleEventName.LoggedOutWatch,
        listing: this.props.listing,
        position: this.props.position,
        componentName: this.props.parentComponentName,
      });
      localStorage.setItem('watch_id', this.props.listing.id);
      this.props.handleLoggedOutUser(evt);
      return;
    }

    this.handleWatch();
  }

  renderText() {
    if (this.props.displayStyle === 'icon') return null;

    return (
      <span className="watch-badge__text">
        {this.getTitle()}
      </span>
    );
  }

  renderNudge() {
    if (!this.props.enableNudge) return null;
    const nudgeText = getWatchNudgeText(this.props.user, this.props.listing);
    if (!nudgeText) return null;

    return (
      <div className="watch-badge__nudge">
        {nudgeText}
      </div>
    );
  }

  render() {
    const { listing, handleLoggedOutUser, displayStyle } = this.props;
    if (this.userLoggedOut() && !handleLoggedOutUser) { return null; }

    const classes = classNames(
      'watch-badge',
      { 'watch-badge--watching': listing.watching },
      { 'watch-badge--button': displayStyle === 'button' },
      { 'watch-badge--text': displayStyle === 'text' },
      { 'watch-badge--icon-only': displayStyle === 'icon' },
    );

    const isWatchIconHeartEnabled = isExperimentEnabled(this.props.user, experiments.WATCH_ICON_HEART);

    return (
      <div className={classes}>
        <button
          className="watch-badge__button"
          onClick={this.handleClick}
          type="button"
          title={this.getTitle()}
        >
          <div className="watch-badge__inner">
            <div className={classNames('watch-badge__icon', { 'watch-badge__icon--heart': isWatchIconHeartEnabled })}>
              {isWatchIconHeartEnabled && <RCIcon
                svgComponent={listing.watching ? HeartFilledSvg : HeartEmptySvg}
                data-watch-heart
              />}
              {!isWatchIconHeartEnabled && <RCIcon
                svgComponent={listing.watching ? StarFilledSvg : StarEmptySvg}
                data-watch-star
              />}
            </div>
            {this.renderText()}
          </div>
        </button>
        {this.renderNudge()}
      </div>
    );
  }
}

export default flowRight([
  withUserContext,
  createWatch,
  deleteWatch,
])(WatchBadge);
